import { UseFormReturn } from 'react-hook-form';
import { CalendarDate, Time } from '@internationalized/date';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage
} from '../../../design-system/Form';
import { Input } from '../../../design-system/Input';
import { DateInput } from '../../../design-system/DateInput';
import { DateRangeInput } from '../../../design-system/DateRangeInput';
import { TimeInput } from '../../../design-system/TimeInput';
import { Tabs, TabsList, TabsTrigger } from '../../../design-system/Tabs';
import type { CallsheetDateSelection } from '../types';

type CallsheetDetailsFormData = {
  title: string;
  dateSelection: CallsheetDateSelection;
  generalCallTime: Time | null;
};

export interface CallsheetDetailsFieldsProps {
  // Accept any form that has the required fields (CallsheetFormData or DuplicateCallsheetFormData)
  form: UseFormReturn<any>;
  /** Optional default date for fallback when switching modes */
  defaultDate?: CalendarDate;
}

/**
 * Reusable form fields for callsheet details:
 * - Title input
 * - Date selection toggle (single/range)
 * - Date/DateRange input
 * - General call time input
 */
export function CallsheetDetailsFields({
  form,
  defaultDate
}: CallsheetDetailsFieldsProps) {
  const dateSelection = form.watch('dateSelection') as CallsheetDateSelection;

  return (
    <div className="space-y-6">
      {/* Title Field */}
      <FormField
        control={form.control}
        name="title"
        rules={{ required: 'Title is required' }}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Title</FormLabel>
            <FormControl>
              <Input {...field} placeholder="Enter callsheet title" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Date Mode Toggle */}
      <FormField
        control={form.control}
        name="dateSelection"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Date selection</FormLabel>
            <FormControl>
              <Tabs
                value={field.value.type}
                onValueChange={value => {
                  if (value === 'range') {
                    const currentDate =
                      field.value.type === 'single'
                        ? field.value.date
                        : field.value.from;
                    field.onChange({
                      type: 'range',
                      from: currentDate,
                      to: currentDate
                    });
                  } else {
                    const currentDate =
                      field.value.type === 'single'
                        ? field.value.date
                        : field.value.from;
                    field.onChange({ type: 'single', date: currentDate });
                  }
                }}
              >
                <TabsList size="sm">
                  <TabsTrigger value="single">Single day</TabsTrigger>
                  <TabsTrigger value="range">Date range</TabsTrigger>
                </TabsList>
              </Tabs>
            </FormControl>
          </FormItem>
        )}
      />

      {/* Date and Time Row */}
      <div className="grid grid-cols-2 gap-4">
        {/* Date Field - Single or Range based on mode */}
        <FormField
          control={form.control}
          name="dateSelection"
          rules={{ required: 'Date is required' }}
          render={({ field }) =>
            field.value.type === 'range' ? (
              <FormItem>
                <FormLabel>Date range</FormLabel>
                <FormControl>
                  <DateRangeInput
                    value={{ from: field.value.from, to: field.value.to }}
                    onChange={range => {
                      if (range) {
                        field.onChange({
                          type: 'range',
                          from: range.from,
                          to: range.to
                        });
                      }
                    }}
                    variant="regular"
                    size="md"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            ) : (
              <FormItem>
                <FormLabel>Date</FormLabel>
                <FormControl>
                  <DateInput
                    value={
                      (field.value.type === 'single'
                        ? field.value.date
                        : defaultDate) as CalendarDate
                    }
                    onChange={date => field.onChange({ type: 'single', date })}
                    variant="regular"
                    size="md"
                    className="w-full"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )
          }
        />

        {/* General Call Time Field */}
        <FormField
          control={form.control}
          name="generalCallTime"
          render={({ field }) => (
            <FormItem>
              <FormLabel>General call time</FormLabel>
              <FormControl>
                <TimeInput
                  value={(field.value as Time | null | undefined) ?? null}
                  onChange={time => field.onChange(time)}
                  placeholder="09:00 AM"
                  variant="regular"
                  size="md"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
